import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';

import { SkeletonTable } from '../SpotlightsSkeleton';
import { SpotlightsSortMenu, SpotlightsFilterMenu, FilterField, FilterOption } from '../SpotlightsMenus';
import MarkSpotlightErroredModal from '../MarkSpotlightErroredModal';
import SelectAllBox from '../SelectAllBox';
import { getTriageQueue, bulkResolveTriage } from '../../../api/spotlights';

// The unified triage queue ("Error section"): open issues an admin should act on — both
// auto-detected operational events (the same SpotlightEvents that drive the Needs Attention report)
// and spotlights an admin manually marked errored. Source = 'admin' vs 'auto'. Resolving an
// admin-flagged item clears the stamp and returns the spotlight to its pipeline; resolving an
// auto-detected one acknowledges it (it reopens on the next detection if the condition persists).
// This is an admin-only surface, so the reason IS shown here.

// Ads Manager deep link for a single ad id — same pattern the brand/creator tabs use.
// Fallback for digest items cached before the server started sending a platform-aware adUrl.
const metaAdUrl = adId => (adId ? `https://business.facebook.com/adsmanager/manage/ads?selected_ad_ids=${adId}` : null);

const REFRESH_MS = 60000;

const SOURCE_LABELS = { admin: 'Admin', auto: 'Auto' };

// Friendly labels for the event `type` shown in the Reason filter and column.
const TYPE_LABELS = {
  admin_flagged: 'Admin flagged',
  pixel_awaiting: 'Pixel awaiting',
  no_delivery: 'No delivery',
  automation_gave_up: 'Automation gave up',
  creative_rejected: 'Creative rejected',
  publish_blocked: 'Publish blocked',
  ad_set_config_blocked: 'Ad set config blocked',
  creator_ineligible: 'Creator ineligible',
  sync_paused_to_match_meta: 'Sync paused',
  sync_repaused_on_meta: 'Sync re-paused',
  sync_repaused_off_phase: 'Sync re-paused (off phase)'
};
const typeLabel = t => TYPE_LABELS[t] || t;

// Severity ⇒ pill styling + relative urgency for sorting (higher = more urgent).
const SEVERITY = {
  blocking: { label: 'Blocking', cls: 'red', rank: 3 },
  attention: { label: 'Attention', cls: 'amber', rank: 2 },
  retrying: { label: 'Retrying', cls: 'blue', rank: 1 },
  info: { label: 'Info', cls: '', rank: 0 }
};

const SOURCE_FILTERS = [
  { key: '', label: 'All' },
  { key: 'admin', label: 'Admin-flagged' },
  { key: 'auto', label: 'Auto-detected' }
];
const SEVERITY_FILTERS = [{ key: '', label: 'All' }, ...Object.keys(SEVERITY).map(k => ({ key: k, label: SEVERITY[k].label }))];
const AGE_FILTERS = [
  { key: '', label: 'Any age' },
  { key: 'lt1', label: 'Today' },
  { key: '1to3', label: '1–3 days' },
  { key: '3to7', label: '3–7 days' },
  { key: 'gt7', label: 'Over a week' }
];

const SORT_OPTIONS = [{ key: 'age', label: 'Age' }, { key: 'severity', label: 'Severity' }, { key: 'source', label: 'Source' }];

const ageDays = createdAt => (createdAt ? (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24) : 0);

const fmtAge = createdAt => {
  const days = Math.floor(ageDays(createdAt));
  if (!createdAt) return '';
  if (days <= 0) return 'today';
  return `${days}d ago`;
};

const inAgeBucket = (createdAt, bucket) => {
  const d = ageDays(createdAt);
  if (bucket === 'lt1') return d < 1;
  if (bucket === '1to3') return d >= 1 && d < 3;
  if (bucket === '3to7') return d >= 3 && d < 7;
  if (bucket === 'gt7') return d >= 7;
  return true;
};

const SpotlightsError = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkResolving, setBulkResolving] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);

  const [source, setSource] = useState('');
  const [severity, setSeverity] = useState('');
  const [type, setType] = useState('');
  const [age, setAge] = useState('');
  const [sortBy, setSortBy] = useState('age');
  const [sortDir, setSortDir] = useState('desc');

  // silent = a background poll: refresh data without flashing the skeleton or dropping selection.
  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await getTriageQueue();
      const next = r.items || [];
      setItems(next);
      // Drop any selection whose item is gone (already resolved elsewhere / self-resolved).
      const live = new Set(next.map(i => i.eventId));
      setSelected(prev => new Set([...prev].filter(id => live.has(id))));
    } catch (e) {
      if (!silent) setError(typeof e === 'string' ? e : 'Failed to load the triage queue.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const resolveIds = async eventIds => {
    if (!eventIds.length) return;
    setBulkResolving(true);
    try {
      const r = await bulkResolveTriage(eventIds);
      cogoToast.success(`Resolved ${r.resolved || eventIds.length}`);
      await load({ silent: true });
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Could not resolve.');
    } finally {
      setBulkResolving(false);
    }
  };

  const toggleOne = id =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Filter → sort. Everything is client-side: the queue is the set of open items, already loaded.
  const visible = items
    .filter(i => (!source || i.source === source) && (!severity || i.severity === severity) && (!type || i.type === type) && inAgeBucket(i.createdAt, age))
    .sort((a, b) => {
      let cmp;
      if (sortBy === 'severity') cmp = (SEVERITY[a.severity]?.rank || 0) - (SEVERITY[b.severity]?.rank || 0);
      else if (sortBy === 'source') cmp = String(a.source).localeCompare(String(b.source));
      else cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const visibleIds = visible.map(i => i.eventId);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someSelected = visibleIds.some(id => selected.has(id));

  const toggleAll = () =>
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });

  // Only the reason types actually present, so the filter never lists empty options.
  const presentTypes = [...new Set(items.map(i => i.type))];
  const activeFilterCount = (source ? 1 : 0) + (severity ? 1 : 0) + (type ? 1 : 0) + (age ? 1 : 0);
  const clearFilters = () => {
    setSource('');
    setSeverity('');
    setType('');
    setAge('');
  };

  return (
    <div className='spotlights-error'>
      <div className='sl-controls'>
        <SpotlightsFilterMenu count={activeFilterCount} onClear={clearFilters}>
          <FilterField label='Source'>
            {SOURCE_FILTERS.map(f => (
              <FilterOption key={f.key} label={f.label} selected={source === f.key} onClick={() => setSource(f.key)} />
            ))}
          </FilterField>
          <FilterField label='Severity'>
            {SEVERITY_FILTERS.map(f => (
              <FilterOption key={f.key} label={f.label} selected={severity === f.key} onClick={() => setSeverity(f.key)} />
            ))}
          </FilterField>
          <FilterField label='Reason'>
            <FilterOption label='All' selected={type === ''} onClick={() => setType('')} />
            {presentTypes.map(t => (
              <FilterOption key={t} label={typeLabel(t)} selected={type === t} onClick={() => setType(t)} />
            ))}
          </FilterField>
          <FilterField label='Age'>
            {AGE_FILTERS.map(f => (
              <FilterOption key={f.key} label={f.label} selected={age === f.key} onClick={() => setAge(f.key)} />
            ))}
          </FilterField>
        </SpotlightsFilterMenu>
        <SpotlightsSortMenu options={SORT_OPTIONS} value={sortBy} dir={sortDir} onChange={(key, d) => { setSortBy(key); setSortDir(d); }} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#767676', fontSize: 13 }}>{visible.length} open</span>
          <button className='sl-btn secondary small' onClick={() => load()} disabled={loading}>
            Refresh
          </button>
          <button className='sl-btn' onClick={() => setMarkOpen(true)}>
            Mark errored
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className='sl-bulk-bar'>
          <span>{selected.size} selected</span>
          <button className='sl-btn small' disabled={bulkResolving} onClick={() => resolveIds([...selected])}>
            {bulkResolving ? 'Resolving…' : 'Resolve selected'}
          </button>
          <button className='sl-btn secondary small' disabled={bulkResolving} onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      {markOpen && <MarkSpotlightErroredModal onClose={() => setMarkOpen(false)} onDone={() => load({ silent: true })} />}

      {error && <div className='sl-error'>{error}</div>}

      {loading && !items.length ? (
        <SkeletonTable rows={8} cols={7} />
      ) : (
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          <table className='sl-table'>
            <thead>
              <tr>
                <th className='no-sort sl-check-col'>
                  <SelectAllBox checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} disabled={!visibleIds.length} />
                </th>
                <th>Spotlight</th>
                <th>Brand × Creator</th>
                <th>Reason</th>
                <th className='no-sort'>Severity</th>
                <th className='no-sort'>Source</th>
                <th className='no-sort'>Age</th>
                <th className='no-sort' style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className='sl-empty'>
                    Nothing needs triage right now. 🎉
                  </td>
                </tr>
              ) : (
                visible.map(item => {
                  const sev = SEVERITY[item.severity] || SEVERITY.info;
                  return (
                    <tr key={item.eventId} className={cn({ 'sl-row-selected': selected.has(item.eventId) })}>
                      <td className='sl-check-col'>
                        <input type='checkbox' checked={selected.has(item.eventId)} onChange={() => toggleOne(item.eventId)} />
                      </td>
                      <td>{item.Spotlight_id ? `#${item.Spotlight_id}` : item.Brand_id ? `Brand ${item.Brand_id}` : '—'}</td>
                      <td>
                        {item.brandName || '?'} {item.creatorName ? `× ${item.creatorName}` : ''}
                      </td>
                      <td>
                        <span className='sl-error-type'>{typeLabel(item.type)}</span>
                        {item.reason ? <span className='sl-error-reason'> — {item.reason}</span> : null}
                      </td>
                      <td>
                        <span className={cn('sl-pill', sev.cls)}>{sev.label}</span>
                      </td>
                      <td>
                        <span className={cn('sl-pill', { red: item.source === 'admin' })}>{SOURCE_LABELS[item.source] || item.source}</span>
                      </td>
                      <td>{fmtAge(item.createdAt)}</td>
                      <td className='sl-error-actions'>
                        {(item.adUrl || metaAdUrl(item.metaAdId)) && (
                          <button
                            className='sl-btn secondary small'
                            onClick={() => window.open(item.adUrl || metaAdUrl(item.metaAdId), '_blank', 'noopener')}
                          >
                            {item.adPlatform === 'tiktok' ? 'TikTok' : 'Meta'}
                          </button>
                        )}
                        {item.Spotlight_id && (
                          <button className='sl-btn secondary small' onClick={() => navigator.clipboard?.writeText(String(item.Spotlight_id))}>
                            Copy ID
                          </button>
                        )}
                        <button className='sl-btn small' onClick={() => resolveIds([item.eventId])} disabled={bulkResolving}>
                          Resolve
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SpotlightsError;
